import React from 'react'
import PropTypes from 'prop-types'
import { Box, Typography, styled } from '@mui/material'
import { useTranslation } from 'react-i18next'
import { useAssociatedLogins, useSwitchPersonaPerson } from 'packages/core'
import { EidAutocomplete } from 'packages/eid-controls'
import { ArrowIcon, Icon } from 'packages/eid-icons'
import Loader from '../Loader'
import WithCloseButton from '../WithCloseButton'
import Avatar from '../Avatar'
import Dropdown from '../Dropdown'

const PersonValueIndicator = styled(WithCloseButton)({
    borderRadius: '0.5rem',
    backgroundColor: '#535b62',
    padding: '1rem 2.2rem 1rem 1.6rem',
    color: '#ffffff',
    display: 'flex',
    alignItems: 'center',
})

const OptionComponent = EidAutocomplete.getOptionComponent({ type: 'person' })

const getOptionLabel = (option) => option.friendlyName

const idPropType = PropTypes.oneOfType([PropTypes.string, PropTypes.number])

const DEFAULT_ASSOCIATED_LOGINS_PATH = '/api/people/AssociatedLogins'
const DEFAULT_PERSON_LOOKUP_PATH = '/api/people'
const DEFAULT_PARAM_TYPE = 'query'

const SwitchPersona = ({
    apiUrl,
    baseEidUrl,
    actPersonId,
    personId,
    associatedLoginsPath = DEFAULT_ASSOCIATED_LOGINS_PATH,
    personLookupPath = DEFAULT_PERSON_LOOKUP_PATH,
    paramType = DEFAULT_PARAM_TYPE,
}) => {
    const { t } = useTranslation()

    const { data: person } = useSwitchPersonaPerson(
        actPersonId,
        personLookupPath,
        paramType,
    )
    const {
        data: associatedLogins,
        isInitialLoading: isLoadingAssociatedLogins,
    } = useAssociatedLogins(associatedLoginsPath)

    const isActingAsAnother =
        actPersonId != null && personId != null && actPersonId !== personId
    const hasAssociatedLogins =
        Array.isArray(associatedLogins) && associatedLogins.length > 0
    const showLoginAsSection = isActingAsAnother || hasAssociatedLogins

    const buildSwitchUrl = (targetPersonId) => {
        const homeReturnUrl = `${window.location.origin}${
            window.config.APP_SUBPATH || ''
        }/`
        return `${baseEidUrl}/WebIdPForms/SwitchPersona/${targetPersonId}?serviceProvider=Portal&QSParam=ReturnUrl=${encodeURIComponent(
            homeReturnUrl,
        )}`
    }

    if (isLoadingAssociatedLogins) {
        return <Loader height="0.5in" />
    }

    if (!showLoginAsSection) {
        return null
    }

    return (
        <Box sx={{ backgroundColor: '#f5f5f7 !important' }}>
            <Dropdown.Item>
                <Icon name="SwitchWithoutCircle" color="#919193" />
                {t(isActingAsAnother ? 'Common_SwitchTo' : 'Common_LoginAs')}
            </Dropdown.Item>
            <Box
                sx={{
                    padding: '0 1rem 1rem 1.6rem',
                    '& input': { height: 'auto !important' },
                }}
                onKeyDown={(e) => e.stopPropagation()}
            >
                {isActingAsAnother ? (
                    <Box
                        style={{
                            minWidth: '21rem',
                            width: 'fit-content',
                            maxWidth: '100%',
                            cursor: 'pointer',
                        }}
                    >
                        <PersonValueIndicator
                            renderCloseButton={false}
                            role="button"
                            tabIndex={0}
                            aria-label={`Switch to ${person?.friendlyName || ''}`}
                            onClick={() => {
                                window.location.href =
                                    buildSwitchUrl(actPersonId)
                            }}
                            onKeyDown={(event) => {
                                if (
                                    event.key === 'Enter' ||
                                    event.key === ' '
                                ) {
                                    event.preventDefault()
                                    window.location.href =
                                        buildSwitchUrl(actPersonId)
                                }
                            }}
                        >
                            <Box
                                style={{
                                    marginRight: '1rem',
                                    backgroundColor: '#535b62',
                                    width: 'auto',
                                }}
                            >
                                <Avatar
                                    src={`${baseEidUrl}${person?.imageThumbUrl}`}
                                />
                            </Box>
                            <Typography
                                style={{
                                    wordBreak: 'break-all',
                                    marginRight: '1rem',
                                }}
                                variant="body1"
                            >
                                {person?.friendlyName}
                            </Typography>
                        </PersonValueIndicator>
                    </Box>
                ) : (
                    <EidAutocomplete.New
                        placeholder={t('SelectPerson')}
                        url={associatedLoginsPath}
                        method="GET"
                        queryParams={{ skip: 0, take: 10 }}
                        dedupingInterval={60000}
                        value={null}
                        getOptionLabel={getOptionLabel}
                        endAdornmentComponent={() => (
                            <ArrowIcon direction="right" color="#453fbb" />
                        )}
                        optionComponent={OptionComponent}
                        showLabel={false}
                        onChange={(_, value) => {
                            if (value) {
                                window.location.href = buildSwitchUrl(value.id)
                            }
                        }}
                    />
                )}
            </Box>
        </Box>
    )
}

SwitchPersona.propTypes = {
    apiUrl: PropTypes.string.isRequired,
    baseEidUrl: PropTypes.string.isRequired,
    /** Persona id for the acting session (from `/api/user` or app-specific source). */
    actPersonId: idPropType,
    /** Logged-in person id to compare for “acting as another” (from same source). */
    personId: idPropType,
    /** GET path under API_URL for associated logins, e.g. `'/api/v1/people/AssociatedLogins'`. */
    associatedLoginsPath: PropTypes.string,
    /** Base path for persona lookup: `query` uses `?personId=`; `route` uses `/{personId}` (e.g. `'/api/v1/people/identities'`). */
    personLookupPath: PropTypes.string,
    /** `'query'` (default) or `'route'` when the id is a route segment instead of a query param. */
    paramType: PropTypes.oneOf(['query', 'route']),
}

export default SwitchPersona
